import type { Media } from "@/payload-types";

type MediaWithSizes = Media & {
  alt?: string;
  sizes?: Record<
    string,
    {
      url?: string | null;
      width?: number | null;
      height?: number | null;
      mimeType?: string;
      filesize?: number;
      filename?: string;
    }
  >;
};

export interface ImageProps {
  url: string;
  width?: number;
  height?: number;
  alt?: string;
  sizes?: string;
}

export type PayloadImageGroup = {
  file?: Media | string | null;
  alt?: string | null;
};

export function getImage(
  imageGroup: PayloadImageGroup,
  fallbackAlt = "",
  preferredSize: string = "card",
  sizes: string = "(max-width: 600px) 100vw, 600px",
): ImageProps {
  const file = imageGroup?.file as MediaWithSizes | undefined;

  // Handle string paths (for Storybook and direct URLs)
  if (typeof file === "string" && file) {
    return {
      url: file,
      alt: imageGroup.alt ?? fallbackAlt,
      sizes,
    };
  }

  if (file && typeof file !== "string" && typeof file.url === "string") {
    // Special handling for "full" size - check if generated size exists first,
    // then fall back to original file if not available
    if (preferredSize === "full") {
      const fullSize = file.sizes?.full;
      if (fullSize && fullSize.url) {
        return {
          url: fullSize.url,
          width: fullSize.width || undefined,
          height: fullSize.height || undefined,
          alt: imageGroup.alt ?? file.alt ?? fallbackAlt,
          sizes,
        };
      }
      // Fall back to original file if generated "full" size doesn't exist
      return {
        url: file.url,
        width: file.width || undefined,
        height: file.height || undefined,
        alt: imageGroup.alt ?? file.alt ?? fallbackAlt,
        sizes,
      };
    }

    // Try each size, fall back to file itself
    const availableSizes = [
      file.sizes?.[preferredSize],
      file.sizes?.thumbnail,
      file.sizes?.medium,
      file.sizes?.hero,
    ];

    // Find first size with a valid url
    const img = availableSizes.find((s) => s && s.url) ?? file;

    // If img.url is null/empty, use base file.url
    const url = img?.url || file.url || "";
    const width = img?.width ?? file.width ?? undefined;
    const height = img?.height ?? file.height ?? undefined;
    const alt = imageGroup.alt ?? file.alt ?? fallbackAlt;

    return {
      url,
      width,
      height,
      alt,
      sizes,
    };
  }

  return {
    url: "",
    alt: imageGroup.alt ?? fallbackAlt,
  };
}
